/**
 * Experience taxonomy — categories & tags a host can pick when adding
 * an experience pin to the guest map.
 *
 * Mirrors the shape of `venueCategories.js` + `venueTags.js` so the UI
 * chip-picker pattern from the venue onboarding maps 1-to-1. Hosts pick
 * exactly ONE category and up to THREE tags underneath that category.
 *
 * Each category has an accent colour used for the pin dot on the guest
 * map, plus an SVG glyph rendered inside the dot. The glyph names map
 * to the icons in `<ExperienceCategoryGlyph>`.
 */

export const EXPERIENCE_CATEGORIES = [
  {
    slug: 'sea-beach',
    display: 'Sea & Beach',
    emoji: '🌊',
    color: '#0EA5E9', // sky-500
    glyph: 'wave',
  },
  {
    slug: 'nature-outdoors',
    display: 'Nature & Outdoors',
    emoji: '🌿',
    color: '#16A34A', // green-600
    glyph: 'mountain',
  },
  {
    slug: 'walking-adventure',
    display: 'Walking & Adventure',
    emoji: '🥾',
    color: '#B45309', // amber-700
    glyph: 'boot',
  },
  {
    slug: 'views-scenic',
    display: 'Views & Scenic Spots',
    emoji: '📸',
    color: '#7C3AED', // violet-600
    glyph: 'camera',
  },
  {
    slug: 'culture-discovery',
    display: 'Culture & Discovery',
    emoji: '🏛️',
    color: '#B91C1C', // red-700
    glyph: 'column',
  },
  {
    slug: 'things-to-do',
    display: 'Things To Do',
    emoji: '🎯',
    color: '#DB2777', // pink-600
    glyph: 'target',
  },
];

export const EXPERIENCE_CATEGORY_MAX = 1;
export const EXPERIENCE_TAG_MAX = 3;
export const EXPERIENCE_DESCRIPTION_MAX = 280;

export const EXPERIENCE_TAGS_BY_CATEGORY = {
  'sea-beach': [
    'Beach', 'Hidden beach', 'Swimming spot', 'Snorkeling spot', 'Sunset spot',
    'Seafront promenade', 'Marina', 'Cliff / coastal viewpoint', 'Beach bar', 'Water sports',
  ],
  'nature-outdoors': [
    'Mountain', 'Hill', 'Forest', 'Lake', 'River',
    'Waterfall', 'Natural viewpoint', 'Nature reserve', 'Cave', 'Botanical garden',
  ],
  'walking-adventure': [
    'Walking path', 'Scenic walk', 'Hiking trail', 'Trekking route', 'Cycling route',
    'Running route', 'Horse riding', 'Rock climbing', 'Kayaking / paddleboarding',
  ],
  'views-scenic': [
    'Viewpoint', 'Panoramic view', 'Sunset view', 'Sunrise view', 'City view',
    'Sea view', 'Mountain view', 'Photo spot', 'Landmark',
  ],
  'culture-discovery': [
    'Historic site', 'Archaeological site', 'Museum', 'Gallery', 'Church / monastery',
    'Traditional village', 'Old town', 'Castle / fortress', 'Local market',
  ],
  'things-to-do': [
    'Boat trip', 'Fishing', 'Diving', 'Surfing', 'Spa / wellness',
    'Golf', 'Tennis', 'Family activity', 'Kids attraction', 'Adventure park',
  ],
};

export const EXPERIENCE_CATEGORY_BY_SLUG = EXPERIENCE_CATEGORIES.reduce((acc, cat) => {
  acc[cat.slug] = cat;
  return acc;
}, {});

export function getExperienceCategory(slug) {
  return EXPERIENCE_CATEGORY_BY_SLUG[slug] || null;
}

export function experienceTagsFor(slug) {
  return EXPERIENCE_TAGS_BY_CATEGORY[slug] || [];
}
